// services/telegramBotService.js - Сервис для интеграции с Telegram Bot API

class TelegramBotService {
    constructor() {
        this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
        this.botToken = process.env.REACT_APP_TELEGRAM_BOT_TOKEN;
        this.telegramAPI = `https://api.telegram.org/bot${this.botToken}`;
    }

    // Получение информации о боте
    async getBotInfo() {
        try {
            const response = await fetch(`${this.baseURL}/telegram/bot-info`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error getting bot info:', error);
            return { success: false, message: 'Ошибка получения информации о боте' };
        }
    }

    // Генерация кода для подключения
    async generateConnectionCode(userId) {
        try {
            const response = await fetch(`${this.baseURL}/telegram/generate-code`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    user_id: userId
                })
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error generating connection code:', error);
            return { success: false, message: 'Ошибка генерации кода подключения' };
        }
    }

    // Проверка подключения пользователя
    async checkConnection(userId) {
        try {
            const response = await fetch(`${this.baseURL}/telegram/check-connection`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    user_id: userId
                })
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error checking connection:', error);
            return { success: false, message: 'Ошибка проверки подключения' };
        }
    }

    // Отключение пользователя от бота
    async disconnectUser(userId) {
        try {
            const response = await fetch(`${this.baseURL}/telegram/disconnect`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    user_id: userId
                })
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error disconnecting user:', error);
            return { success: false, message: 'Ошибка отключения от бота' };
        }
    }

    // Обновление настроек уведомлений
    async updateNotificationSettings(userId, settings) {
        try {
            const response = await fetch(`${this.baseURL}/telegram/notification-settings`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    user_id: userId,
                    settings: settings
                })
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error updating notification settings:', error);
            return { success: false, message: 'Ошибка обновления настроек' };
        }
    }

    // Подключение пользователя к боту
    async connectUserToBot(userId, chatId) {
        try {
            const response = await fetch(`${this.baseURL}/telegram/connect-user`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    user_id: userId,
                    chat_id: chatId
                })
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error connecting user to bot:', error);
            return { success: false, message: 'Ошибка подключения к боту' };
        }
    }

    // Отправка персонального уведомления
    async sendPersonalNotification(userId, message, options = {}) {
        try {
            const response = await fetch(`${this.baseURL}/telegram/send-personal`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    user_id: userId,
                    message: message,
                    options: {
                        parse_mode: options.parse_mode || 'HTML',
                        reply_markup: options.reply_markup || null,
                        disable_web_page_preview: options.disable_web_page_preview || false
                    }
                })
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error sending personal notification:', error);
            return { success: false, message: 'Ошибка отправки уведомления' };
        }
    }

    // Отправка массового уведомления
    async sendBroadcastMessage(message, targetUsers = 'all', options = {}) {
        try {
            const response = await fetch(`${this.baseURL}/telegram/broadcast`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    message: message,
                    target_users: targetUsers, // 'all', 'active', 'verified', array of IDs
                    options: {
                        parse_mode: options.parse_mode || 'HTML',
                        reply_markup: options.reply_markup || null,
                        disable_web_page_preview: options.disable_web_page_preview || false,
                        schedule_time: options.schedule_time || null
                    }
                })
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error sending broadcast:', error);
            return { success: false, message: 'Ошибка массовой отправки' };
        }
    }

    // Получение статистики уведомлений
    async getNotificationStats() {
        try {
            const response = await fetch(`${this.baseURL}/telegram/stats`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error getting notification stats:', error);
            return { success: false, message: 'Ошибка получения статистики уведомлений' };
        }
    }

    // Отправка тестового уведомления
    async sendTestNotification(userId) {
        try {
            const response = await fetch(`${this.baseURL}/telegram/test-notification`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    user_id: userId
                })
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error sending test notification:', error);
            return { success: false, message: 'Ошибка отправки тестового уведомления' };
        }
    }

    // Получение истории уведомлений
    async getNotificationHistory(userId, limit = 20, offset = 0) {
        try {
            const response = await fetch(`${this.baseURL}/telegram/notification-history?user_id=${userId}&limit=${limit}&offset=${offset}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error getting notification history:', error);
            return { success: false, message: 'Ошибка получения истории уведомлений' };
        }
    }

    // Проверка доступности Telegram API
    async checkTelegramAPI() {
        try {
            const response = await fetch(`${this.baseURL}/telegram/check-api`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error checking Telegram API:', error);
            return { success: false, message: 'Ошибка проверки Telegram API' };
        }
    }

    // Получение шаблонов сообщений
    async getMessageTemplates() {
        try {
            const response = await fetch(`${this.baseURL}/telegram/message-templates`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error getting message templates:', error);
            return { success: false, message: 'Ошибка получения шаблонов сообщений' };
        }
    }

    // Создание или обновление шаблона сообщения
    async saveMessageTemplate(templateData) {
        try {
            const response = await fetch(`${this.baseURL}/telegram/message-templates`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(templateData)
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error saving message template:', error);
            return { success: false, message: 'Ошибка сохранения шаблона сообщения' };
        }
    }

    // Планирование отправки сообщения
    async scheduleMessage(userId, message, sendAt, options = {}) {
        try {
            const response = await fetch(`${this.baseURL}/telegram/schedule-message`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    user_id: userId,
                    message: message,
                    send_at: sendAt,
                    options: options
                })
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error scheduling message:', error);
            return { success: false, message: 'Ошибка планирования отправки сообщения' };
        }
    }

    // Отмена запланированного сообщения
    async cancelScheduledMessage(messageId) {
        try {
            const response = await fetch(`${this.baseURL}/telegram/cancel-scheduled/${messageId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error canceling scheduled message:', error);
            return { success: false, message: 'Ошибка отмены запланированного сообщения' };
        }
    }

    // Получение списка запланированных сообщений
    async getScheduledMessages(userId = null) {
        try {
            const url = userId
                ? `${this.baseURL}/telegram/scheduled-messages?user_id=${userId}`
                : `${this.baseURL}/telegram/scheduled-messages`;

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error getting scheduled messages:', error);
            return { success: false, message: 'Ошибка получения запланированных сообщений' };
        }
    }

    // Экспорт настроек пользователя
    async exportUserSettings(userId) {
        try {
            const response = await fetch(`${this.baseURL}/telegram/export-settings/${userId}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error exporting user settings:', error);
            return { success: false, message: 'Ошибка экспорта настроек пользователя' };
        }
    }

    // Импорт настроек пользователя
    async importUserSettings(userId, settingsData) {
        try {
            const response = await fetch(`${this.baseURL}/telegram/import-settings/${userId}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(settingsData)
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error importing user settings:', error);
            return { success: false, message: 'Ошибка импорта настроек пользователя' };
        }
    }

    // Получение аналитики по уведомлениям
    async getNotificationAnalytics(dateFrom, dateTo, userId = null) {
        try {
            const params = new URLSearchParams({
                date_from: dateFrom,
                date_to: dateTo
            });

            if (userId) {
                params.append('user_id', userId);
            }

            const response = await fetch(`${this.baseURL}/telegram/analytics?${params}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error getting notification analytics:', error);
            return { success: false, message: 'Ошибка получения аналитики уведомлений' };
        }
    }

    // Получение информации о веб-хуке
    async getWebhookInfo() {
        try {
            const response = await fetch(`${this.baseURL}/telegram/webhook-info`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error getting webhook info:', error);
            return { success: false, message: 'Ошибка получения информации о веб-хуке' };
        }
    }

    // Проверка здоровья бота
    async checkBotHealth() {
        try {
            const response = await fetch(`${this.baseURL}/telegram/bot-health`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error checking bot health:', error);
            return { success: false, message: 'Ошибка проверки состояния бота' };
        }
    }
}

// Экспорт экземпляра сервиса
export const telegramBotService = new TelegramBotService();
